import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { HiSparkles } from "react-icons/hi2";
import dapple from "../../Image/Client/dapple.svg";
import dplay from "../../Image/Client/dplay.svg";
import dinsta from "../../Image/Client/dinsta.svg";
import dfb from "../../Image/Client/dfb.svg";
import dthr from "../../Image/Client/dthr.svg";

const SOCIALS = [
    {
        icon: dinsta,
        alt: "Instagram",
        url: "https://www.instagram.com/picpromptify/",
        brand: "linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)",
        glow: "rgba(221,42,123,0.5)",
    },
    {
        icon: dfb,
        alt: "Facebook",
        url: "https://www.facebook.com/profile.php?id=61579052481615",
        brand: "linear-gradient(135deg, #1877F2, #4267B2)",
        glow: "rgba(24,119,242,0.5)",
    },
    {
        icon: dthr,
        alt: "Threads",
        url: "https://www.threads.com/@picpromptify",
        brand: "linear-gradient(135deg, #000000, #555555)",
        glow: "rgba(0,0,0,0.6)",
    },
];

const DOWNLOADS = [
    {
        icon: dapple,
        alt: "App Store",
        label: "App Store",
        url: "https://apps.apple.com/in/app/picpromptify/id6749353177",
        brand: "linear-gradient(135deg, #7F00FF, #FF8AFF)",
        glow: "rgba(127,0,255,0.55)",
    },
    {
        icon: dplay,
        alt: "Play Store",
        label: "Play Store",
        url: "https://play.google.com/store/apps/details?id=com.picprompter.pixprompt.prompt.ly.android",
        brand: "linear-gradient(135deg, #FCFA02, #FF8AFF)",
        glow: "rgba(252,250,2,0.45)",
    },
];

const FloatBtn = ({ item, side = "right" }) => {
    const [hover, setHover] = useState(false);
    return (
        <motion.button
            onClick={() => window.open(item.url, "_blank")}
            onHoverStart={() => setHover(true)}
            onHoverEnd={() => setHover(false)}
            whileTap={{ scale: 0.9 }}
            aria-label={item.alt}
            initial='rest'
            animate={hover ? "hover" : "rest"}
            variants={{
                rest: { scale: 1, x: 0 },
                hover: {
                    scale: 1.12,
                    x: side === "right" ? -4 : 4,
                    transition: { type: "spring", stiffness: 340, damping: 16 },
                },
            }}
            className='relative w-11 h-11 rounded-2xl flex items-center justify-center overflow-hidden group'
            style={{
                background: "rgba(255,255,255,0.06)",
                backdropFilter: "blur(14px) saturate(180%)",
                WebkitBackdropFilter: "blur(14px) saturate(180%)",
                border: "1px solid rgba(255,255,255,0.14)",
                boxShadow: hover
                    ? `0 12px 28px -8px ${item.glow}, inset 0 1px 0 rgba(255,255,255,0.22)`
                    : "0 6px 16px -6px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.16)",
            }}
        >
            {/* Brand fill */}
            <motion.div
                aria-hidden
                variants={{
                    rest: { opacity: 0, scale: 0.6 },
                    hover: { opacity: 1, scale: 1.2 },
                }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className='absolute inset-0 pointer-events-none'
                style={{ background: item.brand }}
            />
            {/* Sweep shine */}
            <motion.div
                aria-hidden
                variants={{
                    rest: { x: "-120%", opacity: 0 },
                    hover: { x: "120%", opacity: 1 },
                }}
                transition={{ duration: 0.7, ease: "easeInOut" }}
                className='absolute inset-y-0 w-1/2 pointer-events-none'
                style={{
                    background:
                        "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)",
                    filter: "blur(2px)",
                }}
            />
            <motion.img
                src={item.icon}
                alt={item.alt}
                variants={{
                    rest: { scale: 1 },
                    hover: { scale: 1.18 },
                }}
                transition={{ type: "spring", stiffness: 320, damping: 14 }}
                className='relative w-5 h-5 drop-shadow-md'
            />
            {/* Inner ring */}
            <motion.div
                aria-hidden
                variants={{ rest: { opacity: 0 }, hover: { opacity: 1 } }}
                transition={{ duration: 0.3 }}
                className='absolute inset-0 rounded-2xl pointer-events-none'
                style={{
                    boxShadow: "inset 0 0 0 1.5px rgba(255,255,255,0.3)",
                }}
            />

            {/* Tooltip label on hover (left side) */}
            <AnimatePresence>
                {hover && (
                    <motion.span
                        initial={{ opacity: 0, x: 6, scale: 0.9 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 6, scale: 0.9 }}
                        transition={{ type: "spring", stiffness: 340, damping: 22 }}
                        className='absolute right-full mr-3 px-2.5 py-1 rounded-lg text-[11px] font-semibold tracking-wide whitespace-nowrap pointer-events-none'
                        style={{
                            background: "rgba(18,18,18,0.85)",
                            backdropFilter: "blur(14px)",
                            WebkitBackdropFilter: "blur(14px)",
                            border: "1px solid rgba(255,255,255,0.12)",
                            color: "#fff",
                            boxShadow:
                                "0 8px 20px -8px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.15)",
                        }}
                    >
                        {item.label || item.alt}
                        <span
                            aria-hidden
                            className='absolute top-1/2 -right-1 -translate-y-1/2 w-2 h-2 rotate-45'
                            style={{
                                background: "rgba(18,18,18,0.85)",
                                borderRight: "1px solid rgba(255,255,255,0.12)",
                                borderTop: "1px solid rgba(255,255,255,0.12)",
                            }}
                        />
                    </motion.span>
                )}
            </AnimatePresence>
        </motion.button>
    );
};

const FixedDownload = () => {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);
    if (!mounted) return null;

    const content = (
        <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 26, delay: 0.4 }}
            className='client-fab hidden lg:flex fixed right-5 top-1/2 -translate-y-1/2 flex-col items-center gap-4 z-40'
        >
            {/* Socials cluster */}
            <div
                className='relative flex flex-col items-center gap-2.5 p-2 rounded-3xl'
                style={{
                    background: "rgba(18,18,18,0.45)",
                    backdropFilter: "blur(18px) saturate(180%)",
                    WebkitBackdropFilter: "blur(18px) saturate(180%)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow:
                        "0 14px 36px -14px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12)",
                }}
            >
                {/* Eyebrow */}
                <div
                    className='absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] font-bold tracking-wider uppercase whitespace-nowrap'
                    style={{
                        background:
                            "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                        color: "#fff",
                        boxShadow: "0 4px 10px -2px rgba(127,0,255,0.5)",
                    }}
                >
                    Follow
                </div>
                {SOCIALS.map((s) => (
                    <FloatBtn key={s.alt} item={s} />
                ))}
            </div>

            {/* Downloads cluster */}
            <div
                className='relative flex flex-col items-center gap-2.5 p-2 rounded-3xl'
                style={{
                    background: "rgba(18,18,18,0.45)",
                    backdropFilter: "blur(18px) saturate(180%)",
                    WebkitBackdropFilter: "blur(18px) saturate(180%)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    boxShadow:
                        "0 14px 36px -14px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.12)",
                }}
            >
                <div
                    className='absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[8px] font-bold tracking-wider uppercase whitespace-nowrap inline-flex items-center gap-0.5'
                    style={{
                        background:
                            "linear-gradient(135deg, #FCFA02, #FF8AFF)",
                        color: "#121212",
                        boxShadow: "0 4px 10px -2px rgba(252,250,2,0.4)",
                    }}
                >
                    <HiSparkles className='text-[10px]' />
                    Get App
                </div>
                {DOWNLOADS.map((d) => (
                    <FloatBtn key={d.alt} item={d} />
                ))}
            </div>
        </motion.div>
    );

    return createPortal(content, document.body);
};

export default FixedDownload;
